import { create } from 'zustand';
import { getChatbotConfigSection, updateChatbotConfigSection } from '../utils/chatbotConfigAccess';

interface ThemeSettings {
  mode: 'light' | 'dark';
  showPoweredBy: boolean;
}

const DEFAULT_THEME: ThemeSettings = {
  mode: 'light',
  showPoweredBy: true
};

interface ThemeStore {
  theme: ThemeSettings;
  loading: boolean;
  initialized: boolean;
  setTheme: (theme: Partial<ThemeSettings>, chatbotPublicId?: string) => Promise<void>;
  initializeTheme: (chatbotPublicId: string) => Promise<void>;
}

export const useThemeSettings = create<ThemeStore>((set) => ({
  theme: DEFAULT_THEME,
  loading: false,
  initialized: false,

  setTheme: async (newTheme, chatbotPublicId) => {
    if (!chatbotPublicId) return;

    try {
      set({ loading: true });
      
      // Update using the centralized config access
      const success = await updateChatbotConfigSection(
        'theme_settings',
        newTheme,
        chatbotPublicId
      );

      if (success) {
        set(state => ({ 
          theme: {
            ...state.theme,
            ...newTheme
          },
          loading: false
        }));
      } else {
        throw new Error('Failed to update theme settings');
      }
    } catch (err) {
      console.error('Error saving theme settings:', err);
      set({ loading: false });
    }
  },

  initializeTheme: async (chatbotPublicId) => {
    try {
      set({ loading: true });

      // Get theme settings from the centralized config
      const themeSettings = await getChatbotConfigSection<ThemeSettings>(
        'theme_settings',
        chatbotPublicId
      );

      if (themeSettings) {
        set({ 
          theme: themeSettings,
          initialized: true,
          loading: false
        });
      } else {
        // Use default theme settings if none are found
        set({ 
          theme: DEFAULT_THEME,
          initialized: true,
          loading: false
        });
      }
    } catch (err) {
      console.error('Error loading theme settings:', err);
      set({ 
        theme: DEFAULT_THEME,
        initialized: true,
        loading: false
      });
    }
  }
}));